import Head from 'next/head'
import Link from 'next/link'
import PageIntro from '@helios/PageIntro'
import PageContent from '@helios/PageContent'

import Layout from '@/components/Layout.js'
import {allSites} from '@/utils/domains.js'
import {useSiteHref} from '@/utils/siteHref'

export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {site},
    })),
    fallback: false,
  }
}

export async function getStaticProps({params}) {
  return {
    props: {site: params.site},
  }
}

function Terms({site}) {
  // The policy names the Campus website: a link to its home from every host.
  const campusHome = useSiteHref('campus', '/')

  return (
    <Layout site={site}>
      <div id="wrapper">
        <Head>
          <title>Terms of Use | Campus</title>
          <link rel="canonical" href="https://campus.edu/terms-of-use" />
          <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />
          <meta
            name="description"
            content="Stay informed with Campus' terms of use. Explore our terms for a seamless experience and make the most out of Campus while ensuring your rights are protected."
          />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <PageContent>
          <PageIntro
            title="Terms and Conditions"
            meta="Last updated: February 17, 2026"
          >
            <p>
              Please read these terms and conditions carefully before using the
              Website (as defined below). Please note that these terms only
              apply to the use of the Website. For the terms and conditions
              governing your use of Our Campuswire service, please visit{' '}
              <a
                href="https://campuswire.com/terms"
                rel="noreferrer"
                target="_blank"
              >
                https://campuswire.com/terms
              </a>
              {'.'}
            </p>
          </PageIntro>
          <h3>PLEASE READ THE FOLLOWING TERMS CAREFULLY.</h3>
          <p>
            <strong>BY ACCESSING OR USING THE WEBSITE</strong>, YOU AGREE THAT
            YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION TO YOUR USE OF THE
            WEBSITE, YOU AGREE TO BE BOUND BY, THE FOLLOWING TERMS AND
            CONDITIONS, INCLUDING THE CAMPUS{' '}
            <Link href="/privacy-policy" target="_blank">
              PRIVACY POLICY
            </Link>{' '}
            AND{' '}
            <Link href="/acceptable-use-policy" target="_blank">
              ACCEPTABLE USE POLICY
            </Link>{' '}
            (DEFINED BELOW). IF YOU ARE NOT ELIGIBLE, OR DO NOT AGREE TO THE
            TERMS, THEN YOU DO NOT HAVE OUR PERMISSION TO USE THE WEBSITE. YOUR
            USE OF THE WEBSITE, AND CAMPUS&apos;S PROVISION OF THE WEBSITE TO
            YOU, CONSTITUTES AN AGREEMENT BY CAMPUS AND BY YOU TO BE BOUND BY
            THESE TERMS.
          </p>
          <p>
            YOU AGREE TO RECEIVE TEXTS/CALLS FROM OR ON BEHALF OF CAMPUS AT THE
            PHONE NUMBER YOU PROVIDE TO US. THESE TEXTS/CALLS WILL INCLUDE
            INFORMATIONAL AND PROMOTIONAL MESSAGES WITH INFORMATION ABOUT
            CAMPUS&apos;S PROGRAMS. YOU UNDERSTAND AND AGREE THAT THESE
            TEXTS/CALLS MAY BE CONSIDERED TELEMARKETING UNDER APPLICABLE LAW,
            THEY MAY BE SENT USING AN AUTOMATIC TELEPHONE DIALING SYSTEM OR
            OTHER AUTOMATED TECHNOLOGY, AND YOUR CONSENT IS NOT A CONDITION OF
            ANY PURCHASE.
          </p>
          <h3>Interpretation and Definitions</h3>
          <h4>Interpretation</h4>
          <p>
            The words of which the initial letter is capitalized have meanings
            defined under the section titled “Definitions” below or as otherwise
            defined where they are first used. The terms shall have the same
            meaning regardless of whether they appear in singular or in plural.
          </p>
          <h4>Definitions</h4>
          <p>For the purposes of these Terms and Conditions:</p>
          <ul>
            <li>
              &quot;Affiliate&quot; means an entity that controls, is controlled
              by or is under common control with a party, where
              &quot;control&quot; means ownership of 50% or more of the shares,
              equity interest or other securities entitled to vote for election
              of directors or other managing authority.
            </li>
            <li>&quot;Country&quot; refers to: United States</li>
            <li>
              &quot;Company&quot; (referred to as either &quot;Campus&quot;,
              &quot;the Company&quot;, &quot;We&quot;, &quot;Us&quot;, or
              &quot;Our&quot; in these Terms and Conditions) refers to
              CampusGroup, Inc. of 32 Avenue of the Americas, Suite 1701, New
              York, NY 10013.
            </li>
            <li>
              &quot;Device&quot; means any device that can access the Website
              such as a computer, a cellphone or a digital tablet.
            </li>
            <li>
              &quot;Terms and Conditions&quot; (also referred to as
              &quot;Terms&quot;) mean these Terms and Conditions that form the
              entire agreement between You and the Company regarding the use of
              the Website.
            </li>
            <li>
              &quot;Website&quot; refers to the{' '}
              <a href={campusHome} target="_blank" rel="noreferrer">
                https://campus.edu
              </a>{' '}
              website.
            </li>
            <li>
              &quot;You&quot; refers to the individual accessing or using the
              Website, or the company, or other legal entity on behalf of which
              such individual is accessing or using the Website, as applicable.
            </li>
          </ul>
          <h3>Acknowledgment</h3>
          <p>
            These Terms and Conditions set out the rights and obligations of all
            users regarding the use of the Website.
          </p>
          <p>
            These Terms and Conditions apply to all visitors, users and others
            who access or use the Website.
          </p>
          <p>You represent that you are over the age of 18.</p>
          <p>
            Please read the{' '}
            <Link href="/privacy-policy" target="_blank">
              Campus Privacy Policy
            </Link>{' '}
            and{' '}
            <Link href="/acceptable-use-policy" target="_blank">
              Acceptable Use Policy
            </Link>{' '}
            carefully before using the Website. The Campus Privacy Policy
            describes Our policies and procedures on the collection, use and
            disclosure of Your personal information when You use the Website and
            tells You about Your privacy rights and how the law protects You.
            The Campus Acceptable Use Policy sets forth rules that apply to Your
            use of any of the Company and its consolidated subsidiaries website,
            software, services, or equipment.
          </p>
          <h3>Limited License</h3>
          <p>
            Subject to Your complete and ongoing compliance with these Terms and
            Conditions, the Company grants You, solely for Your personal use, a
            limited, non-exclusive, non-transferable, non-sublicensable,
            revocable license to access and use the Website.
          </p>
          <h3>Intellectual Property</h3>
          <p>
            The Website is owned and operated by the Company. The Website and
            the visual interfaces, graphics, design, compilation, information,
            data, computer code (including source code or object code and all
            content, features, functionality, and elements of the Website)
            (collectively, the &quot;<strong>Content</strong>&quot;) are and
            will remain the exclusive property of the Company and its licensors.
            Except as expressly authorized by the Company, You may not make use
            of the Content. The Company reserves all rights to the Materials not
            granted expressly in these Terms.
          </p>
          <p>
            The Website is protected by copyright, trademark, and other laws of
            both the Country and foreign countries.
          </p>
          <p>
            Our trademarks and trade dress may not be used in connection with
            any product or service without the prior written consent of the
            Company.
          </p>
          <p>
            Except and solely to the extent such a restriction is impermissible
            under applicable law, You may not: (a) reproduce, distribute,
            publicly display, or publicly perform the Website; (b) make
            modifications to the Website; or (c) interfere with or circumvent
            any feature of the Website, including any security or access control
            mechanism. If You are prohibited under applicable law from using the
            Website, You may not use it.
          </p>
          <h3>Information You Submit</h3>
          <p>
            Do not submit any information or other materials that You consider
            confidential or proprietary through the Website. If You choose to
            provide input and suggestions regarding problems with or proposed
            modifications or improvements to the Website (&quot;{' '}
            <strong>Feedback</strong>&quot;), then You do so on a
            non-confidential basis (regardless of any designation or indication
            to the contrary in the submitted information or any accompanying
            correspondence) and You hereby grant Campus an unrestricted,
            perpetual, irrevocable, non-exclusive, fully-paid, royalty-free
            transferable, sublicensable right to exploit the Feedback in any
            manner and for any purpose, including to improve the Website and
            create other products and services.
          </p>
          <h3>Links to Other Websites</h3>
          <p>
            The Website may contain links to third-party websites or services
            that are not owned or controlled by the Company.
          </p>
          <p>
            The Company has no control over, and assumes no responsibility for,
            the content, privacy policies, or practices of any third-party
            websites or services. You further acknowledge and agree that the
            Company shall not be responsible or liable, directly or indirectly,
            for any damage or loss caused or alleged to be caused by or in
            connection with the use of or reliance on any such content, goods,
            or services available on or through any such websites or services.
          </p>
          <p>
            We strongly advise You to read the terms and conditions and privacy
            policies of any third-party websites or services that You visit.
          </p>
          <h3>SMS Messaging and Calling</h3>
          <p>
            By entering Your contact information and clicking
            &quot;Submit,&quot; You agree that Campus may call You and send You
            recurring informational and promotional text messages (including SMS
            and, where supported, Rich Communication Services (RCS) messages)
            with information about Campus programs as part of the services
            described in these Terms and Conditions and the Campus{' '}
            <Link href="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            . You understand and agree that these calls and texts may be sent
            using an autodialer and that Your agreement is not a condition of
            any purchase or condition of using the services. Message frequency
            will vary based on Your interactions with Campus.
          </p>
          <p>
            You may opt out of receiving calls and text (SMS) messages from
            Campus at any time by (i) replying &quot;STOP&quot; to any text
            (SMS) message You receive from Campus or (ii) sending an email to{' '}
            <a href="mailto:privacy@campus.edu">privacy@campus.edu</a>{' '}
            indicating that You no longer wish to receive such calls and/or
            texts, along with Your phone number. Text &quot;HELP&quot; for help.
            Message and data rates may apply, and carriers are not liable for
            delayed or undelivered messages. You acknowledge that opting out of
            receiving calls and text (SMS) messages may impact Your use of the
            services.
          </p>
          <h3>Term; Termination</h3>
          <p>
            These Terms are effective beginning when You accept the Terms or
            first access, or use the Website, and ending when terminated as
            described in this Section titled &quot;Term; Termination&quot;. If
            You violate any provision of these Terms, Your authorization to
            access the Website and these Terms automatically terminate.
          </p>
          <p>
            We may terminate or suspend Your access immediately, without prior
            notice or liability, for any reason whatsoever, including without
            limitation if You breach these Terms and Conditions.
          </p>
          <p>
            Upon termination, Your right to use the Website, including any
            Content, will cease immediately.
          </p>
          <p>
            Upon termination of these Terms: (a) Your license rights will
            terminate and You must immediately cease all use of the Website; (b)
            You will no longer be authorized to access the Website; and (c) the
            following Sections will survive: Interpretation; Definitions;
            Acknowledgement; Intellectual Property; Information You Submit;
            Links to Other Websites; Term and Termination; Indemnity;
            Limitations of Liability; &quot;As Is&quot; and &quot;As
            Available&quot; Disclaimer; Governing Law; Dispute Resolution;
            International Use and European Union (EU) Users; United States Legal
            Compliance; General Terms; Severability; Waiver; Translation
            Interpretation; Modification of the Website; Consent to Electronic
            Communication.
          </p>
          <h3>Indemnity</h3>
          <p>
            To the fullest extent permitted by law, You are responsible for Your
            use of the Website, and You will defend and indemnify Campus and its
            officers, directors, employees, consultants, Affiliates,
            subsidiaries and agents (together, the &quot;{' '}
            <strong>Campus Entities</strong>&quot;) from and against every claim
            brought by a third party, and any related liability, damage, loss,
            and expense, including reasonable attorneys&apos; fees and costs,
            arising out of or connected with: (a) Your unauthorized use of, or
            misuse of, the Website; (b) Your violation of any portion of these
            Terms, any representation, warranty, or agreement referenced in
            these Terms, or any applicable law or regulation; (c) Your violation
            of any third party right, including any intellectual property right
            or publicity, confidentiality, other property, or privacy right; or
            (d) any dispute or issue between You and any third party. We reserve
            the right, at Our own expense, to assume the exclusive defense and
            control of any matter otherwise subject to indemnification by You
            (without limiting Your indemnification obligations with respect to
            that matter), and in that case, You agree to cooperate with Our
            defense of those claims.
          </p>
          <h3>LIMITATION OF LIABILITY</h3>
          <p>
            NOTWITHSTANDING ANY DAMAGES THAT YOU MIGHT INCUR, THE ENTIRE
            LIABILITY OF THE CAMPUS ENTITIES UNDER ANY PROVISION OF THESE TERMS
            AND YOUR EXCLUSIVE REMEDY FOR ALL OF THE FOREGOING SHALL BE LIMITED
            TO THE AMOUNT ACTUALLY PAID BY YOU THROUGH THE WEBSITE OR 100 USD IF
            YOU HAVEN&apos;T PURCHASED ANYTHING THROUGH THE WEBSITE. TO THE
            MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE
            CAMPUS ENTITIES BE LIABLE FOR ANY SPECIAL, INCIDENTAL, INDIRECT, OR
            CONSEQUENTIAL DAMAGES WHATSOEVER (INCLUDING, BUT NOT LIMITED TO,
            DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA OR OTHER INFORMATION, FOR
            BUSINESS INTERRUPTION, FOR PERSONAL INJURY, LOSS OF PRIVACY ARISING
            OUT OF OR IN ANY WAY RELATED TO THE USE OF OR INABILITY TO USE THE
            WEBSITE, THIRD-PARTY SOFTWARE AND/OR THIRD-PARTY HARDWARE USED WITH
            THE WEBSITE, OR OTHERWISE IN CONNECTION WITH ANY PROVISION OF THIS
            TERMS), EVEN IF ANY CAMPUS ENTITIES HAVE BEEN ADVISED OF THE
            POSSIBILITY OF SUCH DAMAGES AND EVEN IF THE REMEDY FAILS OF ITS
            ESSENTIAL PURPOSE.
          </p>
          <p>
            EACH PROVISION OF THESE TERMS THAT PROVIDES FOR A LIMITATION OF
            LIABILITY, DISCLAIMER OF WARRANTIES, OR EXCLUSION OF DAMAGES IS
            INTENDED TO AND DOES ALLOCATE THE RISKS BETWEEN THE PARTIES UNDER
            THESE TERMS. THIS ALLOCATION IS AN ESSENTIAL ELEMENT OF THE BASIS OF
            THE BARGAIN BETWEEN THE PARTIES. EACH OF THESE PROVISIONS IS
            SEVERABLE AND INDEPENDENT OF ALL OTHER PROVISIONS OF THESE TERMS.
            THE LIMITATIONS IN THIS &quot;LIMITATION OF LIABILITY&quot; SECTION
            WILL APPLY EVEN IF ANY LIMITED REMEDY FAILS OF ITS ESSENTIAL
            PURPOSE.
          </p>
          <p>
            SOME STATES DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR
            LIMITATION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES,
            WHICH MEANS THAT SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY. IN
            THESE STATES, EACH PARTY&apos;S LIABILITY WILL BE LIMITED TO THE
            GREATEST EXTENT PERMITTED BY LAW.
          </p>
          <h3>&quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; DISCLAIMER</h3>
          <p>
            THE WEBSITE IS PROVIDED TO YOU &quot;AS IS&quot; AND &quot;AS
            AVAILABLE&quot; AND WITH ALL FAULTS AND DEFECTS WITHOUT WARRANTY OF
            ANY KIND. TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, THE
            COMPANY, ON ITS OWN BEHALF AND ON BEHALF OF THE CAMPUS ENTITIES AND
            ITS AND THEIR RESPECTIVE LICENSORS AND SERVICE PROVIDERS, EXPRESSLY
            DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY OR
            OTHERWISE, WITH RESPECT TO THE WEBSITE, INCLUDING ALL IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            TITLE AND NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF
            COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE OR TRADE PRACTICE.
            WITHOUT LIMITATION TO THE FOREGOING, THE CAMPUS ENTITIES PROVIDE NO
            WARRANTY OR UNDERTAKING, AND MAKE NO REPRESENTATION OF ANY KIND THAT
            THE WEBSITE WILL MEET YOUR REQUIREMENTS, ACHIEVE ANY INTENDED
            RESULTS, BE COMPATIBLE OR WORK WITH ANY OTHER SOFTWARE,
            APPLICATIONS, SYSTEMS OR SERVICES, OPERATE WITHOUT INTERRUPTION,
            MEET ANY PERFORMANCE OR RELIABILITY STANDARDS OR BE ERROR FREE OR
            THAT ANY ERRORS OR DEFECTS CAN OR WILL BE CORRECTED.
          </p>
          <p>
            WITHOUT LIMITING THE FOREGOING, NEITHER THE CAMPUS ENTITIES NOR ANY
            OF THEIR PROVIDERS MAKE ANY REPRESENTATION OR WARRANTY OF ANY KIND,
            EXPRESS OR IMPLIED: (I) AS TO THE OPERATION OR AVAILABILITY OF THE
            WEBSITE, OR THE INFORMATION, CONTENT, AND MATERIALS OR PRODUCTS
            INCLUDED THEREON; (II) THAT THE WEBSITE WILL BE UNINTERRUPTED OR
            ERROR-FREE; (III) AS TO THE ACCURACY, RELIABILITY, OR CURRENCY OF
            ANY INFORMATION OR CONTENT PROVIDED THROUGH THE WEBSITE; OR (IV)
            THAT THE WEBSITE, ITS SERVERS, THE CONTENT, OR E-MAILS SENT FROM OR
            ON BEHALF OF ANY CAMPUS ENTITIES ARE FREE OF VIRUSES, SCRIPTS,
            TROJAN HORSES, WORMS, MALWARE, TIMEBOMBS OR OTHER HARMFUL
            COMPONENTS.
          </p>
          <p>
            NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU
            FROM THE WEBSITE OR FROM ANY CAMPUS ENTITIES OR ANY MATERIALS OR
            CONTENT AVAILABLE THROUGH THE WEBSITE WILL CREATE ANY WARRANTY
            REGARDING ANY OF THE CAMPUS ENTITIES OR THE WEBSITE THAT IS NOT
            EXPRESSLY STATED IN THESE TERMS. THE CAMPUS ENTITIES ARE NOT
            RESPONSIBLE FOR ANY DAMAGE THAT MAY RESULT FROM THE WEBSITE OR YOUR
            USE OF THE WEBSITE. YOU UNDERSTAND AND AGREE THAT YOU USE ANY
            PORTION OF THE WEBSITE AT YOUR OWN DISCRETION AND RISK, AND THAT WE
            ARE NOT RESPONSIBLE FOR ANY DAMAGE TO YOUR PROPERTY (INCLUDING YOUR
            COMPUTER SYSTEM OR MOBILE DEVICE USED IN CONNECTION WITH THE
            WEBSITE) OR ANY LOSS OF DATA.
          </p>
          <p>
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN TYPES OF
            WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A
            CONSUMER, SO SOME OR ALL OF THE ABOVE EXCLUSIONS AND LIMITATIONS MAY
            NOT APPLY TO YOU. BUT IN SUCH A CASE THE EXCLUSIONS AND LIMITATIONS
            SET FORTH IN THIS SECTION SHALL BE APPLIED TO THE GREATEST EXTENT
            ENFORCEABLE UNDER APPLICABLE LAW. Campus does not disclaim any
            warranty or other right that Campus is prohibited from disclaiming
            under applicable law.
          </p>
          <h3>Governing Law</h3>
          <p>
            The laws of the State of New York shall govern these Terms and Your
            use of the Website without regard to conflict of law principles that
            would result in the application of the laws of another jurisdiction.
          </p>
          <p>
            If you are a Brazilian resident, you agree that these Terms and
            Conditions shall be governed by the laws of the Federative Republic
            of Brazil, without regard to any conflict of laws principles that
            may provide the application of the law of another jurisdiction. The
            court of the city where you reside is elected to settle any disputes
            that cannot be resolved amicably.
          </p>
          <h3>Arbitration</h3>
          <p>
            If You have any concern or dispute about the Website, You agree to
            first try to resolve the dispute informally by contacting the
            Company. Subject to the foregoing, any dispute, claim or controversy
            arising out of or relating to these Terms or the breach thereof,
            including the determination of the scope or applicability of this
            agreement to arbitrate, shall be determined, where permitted by law,
            by arbitration in New York County before one arbitrator. The
            arbitration shall be administered by JAMS pursuant to its
            Comprehensive Arbitration Rules and Procedures. Judgment on the
            Award may be entered in any court having jurisdiction. This clause
            shall not preclude parties from seeking provisional remedies in aid
            of arbitration from a court of appropriate jurisdiction.
          </p>
          <h3>Class Action Waiver</h3>
          <p>
            YOU AND CAMPUS AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER
            ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR
            CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
          </p>
          <h3>International Use and European Union (EU) Users</h3>
          <p>
            We make no representation that the Website is appropriate or
            available for use outside of the United States. Access to the
            Website from countries or territories or by individuals where such
            access is illegal is prohibited. If You are a European Union
            consumer, You will benefit from any mandatory provisions of the law
            of the country in which You are resident in.
          </p>
          <h3>United States Legal Compliance</h3>
          <p>
            You represent and warrant that (i) You are not located in a country
            that is subject to the United States government embargo, or that has
            been designated by the United States government as a &quot;terrorist
            supporting&quot; country, and (ii) You are not listed on any United
            States government list of prohibited or restricted parties.
          </p>
          <h3>General</h3>
          <p>
            These Terms, together with the Campus.edu Privacy and any Additional
            Terms (defined below), are the entire and exclusive understanding
            and agreement between You and Campus regarding Your use of the
            Website. You may not assign or transfer these Terms or Your rights
            under these Terms, in whole or in part, by operation of law or
            otherwise, without Our prior written consent. We may assign these
            Terms at any time without notice or consent. The failure to require
            performance of any provision will not affect Our right to require
            performance at any other time after that, nor will a waiver by us of
            any breach or default of these Terms, or any provision of these
            Terms, be a waiver of any subsequent breach or default or a waiver
            of the provision itself. Use of section headers in these Terms is
            for convenience only and will not have any impact on the
            interpretation of any provision. Throughout these Terms the use of
            the word &quot;including&quot; means &quot;including but not limited
            to&quot;.
          </p>
          <h3>Severability</h3>
          <p>
            If any provision of these Terms is held to be unenforceable or
            invalid, such provision will be changed and interpreted to
            accomplish the objectives of such provision to the greatest extent
            possible under applicable law and the remaining provisions will
            continue in full force and effect.
          </p>
          <h3>Waiver</h3>
          <p>
            Except as provided herein, the failure to exercise a right or to
            require performance of an obligation under these Terms shall not
            affect a party&apos;s ability to exercise such right or require such
            performance at any time thereafter, nor shall the waiver of a breach
            constitute a waiver of any subsequent breach.
          </p>
          <h3>Translation Interpretation</h3>
          <p>
            These Terms and Conditions may have been translated if We have made
            them available to You on the Website. To the extent permitted by
            applicable law, You agree that the original English text shall
            prevail in the case of a dispute.
          </p>
          <h3>Changes to These Terms and Conditions</h3>
          <p>
            We reserve the right, at Our sole discretion, to modify or replace
            these Terms on a going-forward basis at any time. If a revision is
            material, We will make reasonable efforts to provide at least 30
            days&apos; notice prior to any new terms taking effect. What
            constitutes a material change will be determined at Our sole
            discretion.
          </p>
          <p>
            Please check these Terms periodically for changes. By continuing to
            access or use the Website after those revisions become effective,
            You agree to be bound by the revised terms. If You do not agree to
            the new terms, in whole or in part, please stop using the Website.
            Except as expressly permitted in this Section titled &quot;Changes
            to These Terms and Conditions&quot;, these Terms may be amended only
            by a written agreement signed by authorized representatives of the
            parties to these Terms. Disputes arising under these Terms will be
            resolved in accordance with the version of these Terms that was in
            effect at the time the dispute arose.
          </p>
          <h3>Modification of the Website</h3>
          <p>
            Campus reserves the right to modify or discontinue the Website at
            any time (including by limiting or discontinuing certain features of
            the Website), temporarily or permanently, without notice to You.
            Campus will have no liability for any change to the Website or any
            suspension or termination of Your access to or use of the Website.
          </p>
          <h3>Additional Terms</h3>
          <p>
            Your use of the Website is subject to all additional terms,
            policies, rules, or guidelines applicable to the Website or certain
            features of the Website that we may post on or link to from the
            Website or that You agree to in connection with certain technology
            or services made available to You by or on behalf of Campus (the
            &quot;<strong>Additional Terms</strong>&quot;). All Additional Terms
            are incorporated by this reference into, and made a part of, these
            Terms.
          </p>
          <h3>Consent to Electronic Communications</h3>
          <p>
            By using the Website, You consent to receiving certain electronic
            communications from us. You agree that any notices, agreements,
            disclosures, or other communications that We send to You
            electronically will satisfy any legal communication requirements,
            including that those communications be in writing.
          </p>
          <h3>Notice to California Residents</h3>
          <p>
            If You are a California resident, under California Civil Code
            Section 1789.3, You may contact the Complaint Assistance Unit of the
            Division of Consumer Services of the California Department of
            Consumer Affairs in writing at 1625 N. Market Blvd., Suite S-202,
            Sacramento, California 95834, or by telephone at{' '}
            <a href="tel:800 952-5210">(800) 952-5210</a> in order to resolve a
            complaint regarding the Website or to receive further information
            regarding use of the Website.
          </p>
          <h3>Contact Us</h3>
          <p>
            If You have any questions about these Terms and Conditions, You can
            contact us by emailing{' '}
            <a href="mailto:contact@campus.edu">contact@campus.edu</a>.
          </p>
        </PageContent>
      </div>
    </Layout>
  )
}

export default Terms
